import * as React from 'react'
import * as PropTypes from 'prop-types'
import { css } from 'glamor'
import { PortalWithState } from 'react-portal'

import { withTheme, IWithThemeProps } from '../../theme'

function isNodeInRoot(node: (Node & ParentNode) | null, root: Node) {
  while (node) {
    if (node === root) {
      return true
    }
    node = node.parentNode // eslint-disable-line no-param-reassign
  }
  return false
}

interface IPortalProps {
  onClose?: () => void
  onKeydown?: (ev: KeyboardEvent) => void
  children: React.ReactNode
}

export const Portal = withTheme(
  class Portal extends React.Component<IPortalProps & IWithThemeProps> {
    static propTypes = {
      onClose: PropTypes.func,
      onKeydown: PropTypes.func,
      children: PropTypes.node,
    }

    static defaultProps = {
      onClose: () => {
        // empty onClose as default
      },
      onKeydown: () => {
        // empty onKeydown as default
      },
    }

    constructor(props: IPortalProps & IWithThemeProps) {
      super(props)

      this.content = React.createRef()
    }

    componentDidMount() {
      if (this.props.onKeydown) {
        window.document.addEventListener('keydown', this.props.onKeydown)
      }
      window.document.addEventListener('mousedown', this.onMouseClickOutside)
    }

    componentWillUnmount() {
      if (this.props.onKeydown) {
        window.document.removeEventListener('keydown', this.props.onKeydown)
      }
      window.document.removeEventListener('mousedown', this.onMouseClickOutside)
    }

    onMouseClickOutside = (e: MouseEvent) => {
      if (this.content.current && isNodeInRoot(e.target as Node & ParentNode, this.content.current)) return
      e.stopPropagation()
      if (this.props.onClose) {
        this.props.onClose()
      }
    }

    content: React.RefObject<HTMLDivElement>

    render() {
      const { children, theme, onClose } = this.props

      const style = {
        zIndex: theme.settings.zLayers.portal,
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
      }

      return (
        <PortalWithState closeOnEsc closeOnOutsideClick defaultOpen onClose={onClose}>
          {({ portal }) =>
            portal(
              // keyboard interactions is enabled by closeOnEsc on PortalWithState
              // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
              <div ref={this.content} {...css(style)} onClick={onClose}>
                {children}
              </div>
            )
          }
        </PortalWithState>
      )
    }
  }
)
